import { useEffect, useRef, useState } from 'react';

import { LoaderFunctionArgs, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { PaginationState } from '@tanstack/react-table';

import {
  Button,
  Center,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Spacer,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useBoolean,
  useDisclosure,
} from '@chakra-ui/react';

import { QueryClient, useQueryClient } from '@tanstack/react-query';

import {
  ArchiveOutline,
  Badge,
  EditOutline,
  ArrowLeftOutline,
  DeleteOutline,
  SearchNormalOutline,
} from '@aslanpay/portal-common-lib';

import CompanyDeductionReportsTab from 'app/components/DeductionReports/CompanyDeductionReportsTab';
import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';
import ArchivePayCalendarModal from 'app/components/PayCalendars/ArchivePayCalendar/ArchivePayCalendarModal';
import DeletePayCalendarModal from 'app/components/PayCalendars/DeletePayCalendar/DeletePayCalendarModal';

import {
  PayCalendarProfileTab,
  PayCalendarProfileFormTab,
  PayCalendarEmployeesTab,
  PayCalendarPayCyclesTab,
} from 'app/components/PayCalendars/PayCalendarProfile';

import { PayCalendarProfileTabs } from 'app/constants/tabs';
import { defaultPageSize } from 'app/constants/table';

import useFetchPayCalendar, { fetchPayCalendarQuery } from 'app/hooks/useFetchPayCalendar';
import useDebounce from 'app/hooks/useDebounce';

import { getPayCalendarsPath } from 'app/utils/routes';
import { isPayCalendarArchived } from 'app/utils/payCalendars';
import useAuthContext from 'app/hooks/useAuthContext';
import useFetchCompany from 'app/hooks/useFetchCompany';
import useFetchCompanyConfiguration from 'app/hooks/useFetchCompanyConfiguration';

export const payCalendarProfileLoader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) => {
    const query = fetchPayCalendarQuery(params.payCalendarId);
    return queryClient.getQueryData(query.queryKey) ?? (await queryClient.fetchQuery(query));
  };

function PayCalendarProfile() {
  const submitButtonRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  const { payCalendarId, companyId } = useParams();

  const { user } = useAuthContext();

  const { data: company } = useFetchCompany(companyId || user?.companyId);

  const { data: companyConfiguration } = useFetchCompanyConfiguration(company?.id || '');

  const queryClient = useQueryClient();

  const {
    isOpen: isArchivePayCalendarOpen,
    onOpen: onOpenArchivePayCalendar,
    onClose: onCloseArchivePayCalendar,
  } = useDisclosure();

  const {
    isOpen: isDeletePayCalendarOpen,
    onOpen: onOpenDeletePayCalendar,
    onClose: onCloseDeletePayCalendar,
  } = useDisclosure();

  const [searchParams, setSearchParams] = useSearchParams();

  const currentTabIndex =
    Object.values(PayCalendarProfileTabs).find((tab) => tab.label === searchParams.get('tab'))
      ?.index || PayCalendarProfileTabs.GENERAL.index;

  const { data: payCalendar, isInitialLoading: isLoadingPayCalendar } =
    useFetchPayCalendar(payCalendarId);

  const isArchived = isPayCalendarArchived(payCalendar);
  const hasEmployees = !!payCalendar && payCalendar.employees > 0;

  const handleArchivePayCalendar = () => {
    queryClient.invalidateQueries({ queryKey: ['fetchPayCalendar', payCalendarId] });
    queryClient.invalidateQueries({ queryKey: ['fetchPayCalendarsPage'] });
    queryClient.invalidateQueries({ queryKey: ['fetchPayCalendarsForEmployee'] });
  };

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });

  const [searchValue, setSearchValue] = useState<string>('');

  const handleSearchValueChange = (value: string) => {
    setSearchValue(value);
    setPagination({ pageIndex: 0, pageSize: defaultPageSize });
  };

  const onSearchValueChangedDebounced = useDebounce(handleSearchValueChange);

  const [editMode, setEditMode] = useBoolean();
  const [tabIndex, setTabIndex] = useState(currentTabIndex);
  const [isTableInEditMode, setIsTableInEditMode] = useState(false);

  const handleTabsChange = (index: number) => {
    setTabIndex(index);
    setSearchValue('');
  };

  const handleToggleEditMode = () => {
    setSearchValue('');
    setEditMode.toggle();
  };

  const handleClickBack = () => navigate(-1);

  const previousTabIndex = useRef(tabIndex);

  useEffect(() => {
    const isNewTab = previousTabIndex.current !== tabIndex;
    previousTabIndex.current = tabIndex;

    const params = isNewTab ? new URLSearchParams() : new URLSearchParams(searchParams);
    params.set(
      'tab',
      Object.values(PayCalendarProfileTabs).find((tab) => tab.index === tabIndex)?.label ||
        PayCalendarProfileTabs.GENERAL.label
    );

    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: true });
    }
  }, [tabIndex, searchParams, setSearchParams]);

  return (
    <AuthenticatedPageWrapper title="Pay Calendar">
      {isLoadingPayCalendar ? (
        <Center width="full" height="full" flexGrow={1}>
          <Spinner color="brand.purple" size="xl" mb="50px" />
        </Center>
      ) : (
        <>
          <Button
            my="10px"
            alignSelf="flex-start"
            size="sm"
            variant="link"
            leftIcon={<ArrowLeftOutline boxSize="20px" />}
            onClick={handleClickBack}
          >
            Back
          </Button>
          <Flex gap="20px" alignItems="flex-start" ref={submitButtonRef}>
            <Text as="h2" textStyle="h2" color="brand.darkPurple">
              {payCalendar?.name}
            </Text>
            {isArchived && (
              <Badge status="disabled" size="md" value="Archived" alignSelf="center" />
            )}
            <Spacer />
            {(tabIndex === 0 || tabIndex === 1) && !editMode && (
              <>
                <Button
                  variant="outline"
                  leftIcon={<ArchiveOutline boxSize="20px" />}
                  size="sm"
                  onClick={onOpenArchivePayCalendar}
                  isDisabled={hasEmployees || isArchived}
                >
                  Archive
                </Button>
                <Button
                  variant="danger"
                  leftIcon={<DeleteOutline boxSize="20px" />}
                  size="sm"
                  onClick={onOpenDeletePayCalendar}
                  isDisabled={hasEmployees || isArchived}
                >
                  Delete
                </Button>
                <Button
                  leftIcon={<EditOutline boxSize="20px" />}
                  size="sm"
                  onClick={handleToggleEditMode}
                  isDisabled={isArchived}
                >
                  Edit
                </Button>
                <ArchivePayCalendarModal
                  selectedPayCalendars={[payCalendar?.id || '']}
                  isOpen={isArchivePayCalendarOpen}
                  onClose={onCloseArchivePayCalendar}
                  onArchive={handleArchivePayCalendar}
                />
                <DeletePayCalendarModal
                  selectedPayCalendars={[payCalendar?.id || '']}
                  isOpen={isDeletePayCalendarOpen}
                  onClose={onCloseDeletePayCalendar}
                  onDelete={() => navigate(getPayCalendarsPath())}
                />
              </>
            )}
          </Flex>

          <Tabs mt="10px" index={tabIndex} onChange={handleTabsChange}>
            <Flex align="center" justify="space-between" gap="30px">
              <TabList>
                <Tab isDisabled={editMode || isTableInEditMode}>General</Tab>
                <Tab isDisabled={editMode || isTableInEditMode}>Configuration</Tab>
                <Tab isDisabled={editMode || isTableInEditMode}>Pay cycles list</Tab>
                <Tab isDisabled={editMode || isTableInEditMode}>Employees</Tab>
                <Tab isDisabled={editMode || isTableInEditMode}>Deduction Reports</Tab>
              </TabList>
              <Spacer />
              {tabIndex === 3 && (
                <InputGroup
                  w="278px"
                  aria-disabled={!searchValue.length && (payCalendar?.employees || 0) < 1}
                  color="brand.darkGray"
                  _disabled={{ color: 'system.disabled' }}
                >
                  <Input
                    isDisabled={!searchValue.length && (payCalendar?.employees || 0) < 1}
                    onChange={(event) => onSearchValueChangedDebounced(event.target.value)}
                    type="text"
                    placeholder="Search"
                  />
                  <InputRightElement w="56px" h="full">
                    <SearchNormalOutline boxSize="24px" />
                  </InputRightElement>
                </InputGroup>
              )}
            </Flex>
            <TabPanels mt="40px">
              <TabPanel>
                {editMode && tabIndex === 0 ? (
                  <PayCalendarProfileFormTab
                    ref={submitButtonRef}
                    payCalendar={payCalendar}
                    onCloseEditMode={setEditMode.toggle}
                    companyConfiguration={companyConfiguration}
                    section="general"
                  />
                ) : (
                  <PayCalendarProfileTab
                    payCalendar={payCalendar}
                    companyConfiguration={companyConfiguration}
                    section="general"
                  />
                )}
              </TabPanel>
              <TabPanel>
                {editMode && tabIndex === 1 ? (
                  <PayCalendarProfileFormTab
                    ref={submitButtonRef}
                    payCalendar={payCalendar}
                    onCloseEditMode={setEditMode.toggle}
                    companyConfiguration={companyConfiguration}
                    section="configuration"
                  />
                ) : (
                  <PayCalendarProfileTab
                    payCalendar={payCalendar}
                    companyConfiguration={companyConfiguration}
                    section="configuration"
                  />
                )}
              </TabPanel>
              <TabPanel>
                <PayCalendarPayCyclesTab
                  payCalendarId={payCalendar?.id}
                  payCalendarLocation={payCalendar?.location}
                  payFrequency={payCalendar?.payFrequency}
                  isReadOnly={isArchived}
                  setIsTableInEditMode={setIsTableInEditMode}
                />
              </TabPanel>
              <TabPanel>
                <PayCalendarEmployeesTab
                  payCalendarId={payCalendar?.id}
                  companyId={payCalendar?.companyId}
                  searchValue={searchValue}
                  pagination={{ pageIndex, pageSize }}
                  onPaginationChange={setPagination}
                />
              </TabPanel>
              <TabPanel>
                {tabIndex === PayCalendarProfileTabs.DEDUCTION_REPORTS.index && payCalendar?.id && (
                  <CompanyDeductionReportsTab
                    payCalendarId={payCalendar.id}
                    companyId={payCalendar.companyId}
                  />
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </>
      )}
    </AuthenticatedPageWrapper>
  );
}

export default PayCalendarProfile;
